import React from "react";
import PieChart from "../../components/charts/PieChart";

interface SessionBreakdown {
  type: string;
  count: number;
}

interface TopUser {
  name: string;
  sessions_count: number;
  user_type: string;
  total_revenue: number;
}

interface Session {
  id: string | null;
  session_id: string;
  user_name: string;
  user_type: string;
  status: string;
  started_at: string;
  duration_minutes: number;
  energy_kwh: number;
  amount: number;
}

interface SessionsResponse {
  metrics: {
    total_sessions_today: number;
    total_sessions_week: number;
    total_sessions_month: number;
  };
  session_breakdown: SessionBreakdown[];
  top_users: TopUser[];
  sessions: Session[];
}

interface SessionsTabProps {
  data: SessionsResponse;
}

const SessionsTab: React.FC<SessionsTabProps> = ({ data }) => {
  const pieData = data?.session_breakdown?.map((item) => ({
    label: item.type,
    value: item.count,
    color: "#" + Math.floor(Math.random() * 16777215).toString(16),
  }));
  const formatDuration = (totalMinutes: number) => {
    if (!totalMinutes || totalMinutes <= 0) return "0m";

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (hours > 0 && minutes > 0) return `${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h`;
    return `${minutes}m`;
  };
  return (
    <div className="sessions-tab">
      <div className="sessions-header-cards">
        <div className="session-stat-card">
          <div className="stat-icon">👥</div>
          <div className="stat-info">
            <div className="stat-value">
              {data?.metrics?.total_sessions_today}
            </div>
            <div className="stat-label">Total sessions today</div>
          </div>
        </div>
        <div className="session-stat-card">
          <div className="stat-icon">📅</div>
          <div className="stat-info">
            <div className="stat-value">{data?.metrics?.total_sessions_week}</div>
            <div className="stat-label">Total sessions this week</div>
          </div>
        </div>
        <div className="session-stat-card">
          <div className="stat-icon">📊</div>
          <div className="stat-info">
            <div className="stat-value">
              {data?.metrics?.total_sessions_month}
            </div>
            <div className="stat-label">Total sessions this month</div>
          </div>
        </div>
      </div>
      <div className="sessions-content-grid">
        <div className="session-breakdown-card">
          <h3>Session type breakdown</h3>
          <PieChart data={pieData} radius={100} />
          <div className="pie-legend">
            {data?.session_breakdown?.map((item, idx) => (
              <div key={idx} className="pie-legend-item">
                <span
                  className="legend-dot"
                  style={{ background: pieData[idx].color }}
                ></span>
                <span>
                  {item.type} {item.count}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="top-users-card">
          <h3>Top users & fleets</h3>
          <div
            className="top-users-list scrollable-list"
            style={{ maxHeight: "400px", overflowY: "auto" }}
          >
            {data?.top_users?.map((user, idx) => (
              <div key={idx} className="top-user-item">
                <div className="user-info">
                  <div className="user-name">{user.name}</div>
                  <div className="user-sessions">
                    {user.sessions_count} sessions | {user.user_type}
                  </div>
                </div>
                <div className="user-amount">
                  ₹{user.total_revenue.toFixed(2)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="all-sessions-card">
        <h3>Recent sessions</h3>
        <div
          className="sessions-table scrollable-list"
          style={{ maxHeight: "500px", overflowY: "auto" }}
        >
          {data?.sessions?.map((session) => (
            <div key={session.session_id} className="session-row">
              <div className="session-col">
                <div className="session-name-main">{session.user_name}</div>
                <div className="session-time">
                  Started {new Date(session.started_at).toLocaleString()} |
                  Duration:  {formatDuration(session.duration_minutes)}
                  <div className="session-id">
                    Session ID: {session.session_id} | Energy:{" "}
                    {session.energy_kwh.toFixed(2)} kWh
                  </div>
                </div>
              </div>
              <div
                className={`session-col-right status-badge-table ${session.status.toLowerCase()}`}
              >
                <span>
                  {session.status.charAt(0).toUpperCase() +
                    session.status.slice(1)}
                </span>
              </div>
              <div className="session-amount-col">
                <div className="amount-large">₹{session.amount.toFixed(2)}</div>
                {/* <button className="view-details-link">View details</button> */}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SessionsTab;
